import { useState, useRef, useCallback, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/shadcn/dialog'
import { Button } from '@/components/ui/shadcn/button'
import { Upload, Film, Wand2, Pencil, X, Loader2, ImageIcon, ChevronLeft } from 'lucide-react'
import { type ThumbnailCanvas } from '@backend/lib/db/typeRoots/workspaceMedia'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  thumbnail: ThumbnailCanvas | null | undefined
  videoUrl: string | null
  onThumbnailSet: (url: string, key: string, sourceTimestampMs?: number) => void
  onThumbnailClear: () => void
  onUploadFile: (file: File) => Promise<{ url: string; key: string }>
  onAutoDesign?: () => void
  onOpenEditor?: () => void
}

type PickerMode = 'menu' | 'frame-picker'

export function VideoThumbnailPicker({
  open,
  onOpenChange,
  thumbnail,
  videoUrl,
  onThumbnailSet,
  onThumbnailClear,
  onUploadFile,
  onAutoDesign,
  onOpenEditor,
}: Props) {
  const [mode, setMode] = useState<PickerMode>('menu')
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleClose = (open: boolean) => {
    if (!open) setMode('menu')
    onOpenChange(open)
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''

    setIsUploading(true)
    try {
      const { url, key } = await onUploadFile(file)
      onThumbnailSet(url, key)
      handleClose(false)
    } catch {
      // error handled by parent
    } finally {
      setIsUploading(false)
    }
  }

  if (!videoUrl) return null

  const hasThumbnail = !!thumbnail

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[90dvh] gap-0 overflow-y-auto p-0 sm:max-w-[440px]">
        <DialogHeader className="px-5 pb-0 pt-5">
          <DialogTitle className="flex items-center gap-2 text-base">
            <ImageIcon className="text-primary h-4 w-4" />
            Video Thumbnail
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-xs">
            {hasThumbnail
              ? 'Your custom thumbnail will be used across all platforms that support it. TikTok only supports picking a frame from the video. YouTube requires a verified channel for custom thumbnails.'
              : 'Choose how you want to set your video thumbnail. Note: TikTok only supports picking a frame from the video, and YouTube requires a verified channel for custom thumbnails.'}
          </DialogDescription>
        </DialogHeader>

        {mode === 'menu' && (
          <div className="flex flex-col gap-4 p-5">
            {/* Current thumbnail preview */}
            {hasThumbnail && (
              <div
                className="bg-muted group relative mx-auto max-h-[40dvh] overflow-hidden rounded-lg"
                style={{ aspectRatio: thumbnail!.aspectRatio.replace(':', ' / ') }}>
                <img
                  src={thumbnail!.file.url}
                  alt="Current thumbnail"
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-black/60 to-transparent px-3 pb-2.5 pt-6">
                  <span className="text-xs font-medium text-white/80">Current thumbnail</span>
                  <button
                    onClick={() => {
                      onThumbnailClear()
                      handleClose(false)
                    }}
                    className="flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 text-xs text-white backdrop-blur-sm transition-colors hover:bg-white/25">
                    <X className="h-3 w-3" />
                    Remove
                  </button>
                </div>
              </div>
            )}

            {/* Action grid */}
            <div className="grid grid-cols-2 gap-2">
              <ThumbnailAction
                icon={<Upload className="h-4 w-4" />}
                label="Upload image"
                description="JPG or PNG"
                loading={isUploading}
                onClick={() => fileInputRef.current?.click()}
              />
              <ThumbnailAction
                icon={<Film className="h-4 w-4" />}
                label="Pick a frame"
                description="From video"
                onClick={() => setMode('frame-picker')}
              />
              {onAutoDesign && (
                <ThumbnailAction
                  icon={<Wand2 className="h-4 w-4" />}
                  label="Auto-design"
                  description="AI generated"
                  onClick={() => {
                    onAutoDesign()
                    handleClose(false)
                  }}
                />
              )}
              {onOpenEditor && (
                <ThumbnailAction
                  icon={<Pencil className="h-4 w-4" />}
                  label="Design in editor"
                  description="Full control"
                  onClick={() => {
                    onOpenEditor()
                    handleClose(false)
                  }}
                />
              )}
            </div>
          </div>
        )}

        {mode === 'frame-picker' && (
          <VideoFramePicker
            videoUrl={videoUrl}
            onCapture={async (file, timestampMs) => {
              setIsUploading(true)
              try {
                const { url, key } = await onUploadFile(file)
                onThumbnailSet(url, key, timestampMs)
                handleClose(false)
              } finally {
                setIsUploading(false)
              }
            }}
            onBack={() => setMode('menu')}
            isUploading={isUploading}
          />
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png"
          className="hidden"
          onChange={handleFileSelect}
        />
      </DialogContent>
    </Dialog>
  )
}

function ThumbnailAction({
  icon,
  label,
  description,
  loading,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  description: string
  loading?: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="border-border hover:border-primary/30 hover:bg-primary/[0.03] group flex items-center gap-3 rounded-lg border p-3 text-left transition-all disabled:opacity-50">
      <div className="bg-muted group-hover:bg-primary/10 group-hover:text-primary text-muted-foreground flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : icon}
      </div>
      <div className="min-w-0">
        <p className="text-foreground text-sm font-medium leading-tight">{label}</p>
        <p className="text-muted-foreground text-xs leading-tight">{description}</p>
      </div>
    </button>
  )
}

function VideoFramePicker({
  videoUrl,
  onCapture,
  onBack,
  isUploading,
}: {
  videoUrl: string
  onCapture: (file: File, timestampMs: number) => void
  onBack: () => void
  isUploading: boolean
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [canCapture, setCanCapture] = useState(true)

  // Fetch video as blob to create a same-origin URL — required for
  // canvas.drawImage() to work without tainting the canvas (S3 URLs
  // are cross-origin and the bucket may not send CORS headers).
  // Skip for large videos (>100MB) to avoid consuming too much memory.
  const MAX_BLOB_SIZE = 100 * 1024 * 1024

  const fetchBlob = useCallback(async () => {
    try {
      const head = await fetch(videoUrl, { method: 'HEAD' })
      const size = parseInt(head.headers.get('content-length') || '0', 10)
      if (size > MAX_BLOB_SIZE) {
        // Too large — fall back to direct URL (scrubbing works but capture won't)
        setBlobUrl(videoUrl)
        setCanCapture(false)
        return
      }
      const res = await fetch(videoUrl, { cache: 'no-store' })
      const blob = await res.blob()
      setBlobUrl(URL.createObjectURL(blob))
    } catch {
      setBlobUrl(videoUrl)
      setCanCapture(false)
    }
  }, [videoUrl])

  useEffect(() => {
    fetchBlob()
    return () => {
      setBlobUrl((prev) => {
        if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev)
        return null
      })
    }
  }, [fetchBlob])

  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current
    if (!video) return
    setDuration(video.duration)
    video.currentTime = Math.min(1, video.duration)
  }, [])

  const handleSeek = (value: number) => {
    const video = videoRef.current
    if (!video) return
    video.currentTime = value
    setCurrentTime(value)
  }

  const handleCapture = () => {
    const video = videoRef.current
    if (!video) return

    const timestampMs = Math.round(video.currentTime * 1000)
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.drawImage(video, 0, 0)
    canvas.toBlob(
      (blob) => {
        if (!blob) return
        const file = new File([blob], 'thumbnail.jpg', { type: 'image/jpeg' })
        onCapture(file, timestampMs)
      },
      'image/jpeg',
      0.9,
    )
  }

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60)
    const s = Math.floor(seconds % 60)
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  return (
    <div className="flex flex-col">
      {/* Video viewport */}
      <div className="relative bg-black">
        {blobUrl ? (
          <video
            ref={videoRef}
            src={blobUrl}
            crossOrigin="anonymous"
            className="mx-auto block max-h-[280px]"
            onLoadedMetadata={handleLoadedMetadata}
            onTimeUpdate={() => setCurrentTime(videoRef.current?.currentTime ?? 0)}
          />
        ) : (
          <div className="flex h-[280px] w-full items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-white/50" />
          </div>
        )}
      </div>

      {/* Scrubber */}
      <div className="px-5 pt-3">
        <div
          className="group relative h-6 cursor-pointer"
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect()
            const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
            handleSeek(pct * duration)
          }}>
          {/* Track */}
          <div className="bg-border absolute top-1/2 h-1 w-full -translate-y-1/2 rounded-full">
            <div
              className="bg-primary h-full rounded-full transition-[width] duration-75"
              style={{ width: `${progress}%` }}
            />
          </div>
          {/* Thumb */}
          <div
            className="bg-primary absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full shadow-sm ring-2 ring-white transition-[left] duration-75 dark:ring-black"
            style={{ left: `${progress}%` }}
          />
          {/* Invisible wider hit area for drag */}
          <input
            type="range"
            min={0}
            max={duration || 1}
            step={0.05}
            value={currentTime}
            onChange={(e) => handleSeek(parseFloat(e.target.value))}
            className="absolute inset-0 cursor-pointer opacity-0"
          />
        </div>

        <div className="text-muted-foreground flex justify-between pt-0.5 text-[10px] tabular-nums">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>

      {/* Footer */}
      <div className="border-border mt-2 flex items-center justify-between border-t px-5 py-3">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1">
          <ChevronLeft className="h-3.5 w-3.5" />
          Back
        </Button>
        <Button size="sm" onClick={handleCapture} disabled={isUploading || !blobUrl || !canCapture}>
          {isUploading ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving...
            </>
          ) : !canCapture ? (
            'Video too large for frame capture'
          ) : (
            'Use this frame'
          )}
        </Button>
      </div>
    </div>
  )
}
